import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";

export default function MestimateChart({
  compsUsed = [],
  mestimate = null,
  height = 210,
  title = "Comparable sold prices",
}) {
  const data = useMemo(() => {
    return (compsUsed || [])
      .filter((c) => c && c.price)
      .map((c) => {
        const d = c.soldDate ? new Date(c.soldDate) : null;
        return {
          soldDate: d,
          price: Number(c.price),
          label: `${c.year} ${c.make} ${c.model}${c.trim ? ` ${c.trim}` : ""}`,
          mestimate: typeof mestimate === "number" ? Number(mestimate) : null,
        };
      })
      .sort((a, b) => (a.soldDate || 0) - (b.soldDate || 0))
      .map((c, index) => ({ ...c, compLabel: `Comp ${index + 1}` }));
  }, [compsUsed, mestimate]);

  const range = useMemo(() => {
    if (!compsUsed?.length) return null;
    const nums = compsUsed.map((c) => +c.price || 0).filter(Boolean);
    if (!nums.length) return null;
    return {
      min: Math.min(...nums),
      max: Math.max(...nums),
      count: nums.length,
    };
  }, [compsUsed]);

  if (!data.length) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <div className="mt-1 text-sm text-slate-500">Not enough comparable sales to chart yet.</div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-slate-900">{title}</div>
          <div className="text-xs text-slate-500">Sold comps and estimate trend</div>
        </div>
        {range && (
          <div className="text-xs text-slate-600">
            {`Comps used: ${range.count} · Range: $${range.min.toLocaleString()}-$${range.max.toLocaleString()}`}
          </div>
        )}
      </div>

      <div className="mt-3" style={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis
              dataKey="compLabel"
              tick={{ fontSize: 10, fill: "#64748b" }}
              interval="preserveStartEnd"
              minTickGap={12}
            />
            <YAxis
              tick={{ fontSize: 10, fill: "#64748b" }}
              tickFormatter={(v) => `$${Number(v).toLocaleString()}`}
              width={56}
            />
            <Tooltip
              contentStyle={{ borderRadius: 12, borderColor: "#e2e8f0" }}
              formatter={(value, name, props) => {
                if (name === "price") return [`$${Number(value).toLocaleString()}`, props.payload.label];
                if (name === "mestimate") return [`$${Number(value).toLocaleString()}`, "Mestimate"];
                return [value, name];
              }}
              labelFormatter={(label) => label}
            />

            <Line type="monotone" dataKey="price" name="Comp Price" stroke="#2563eb" strokeWidth={2.5} dot={false} />

            {typeof mestimate === "number" && (
              <Line
                type="monotone"
                dataKey="mestimate"
                name="Mestimate"
                stroke="#ef4444"
                strokeDasharray="5 5"
                dot={false}
                strokeWidth={2}
              />
            )}

            {typeof mestimate === "number" && (
              <ReferenceLine
                y={mestimate}
                stroke="#ef4444"
                strokeDasharray="4 4"
                label={{
                  value: `Mestimate $${Number(mestimate).toLocaleString()}`,
                  position: "insideTopRight",
                  fontSize: 10,
                }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-3 text-xs text-slate-500">
        Based on recent, similar vehicles; extreme outliers removed.
      </div>
    </div>
  );
}
